import type { MasteryLevel } from "./enums";

export type StandardNode = {
  id: string; // real CCSS code, e.g. "3.NF.A.2" — the student.cells join key
  s: string;
  desc: string;
};

// The only place the 10 grade values are listed — Grade is derived from it,
// so there's one source of truth instead of two that could drift apart.
export const GRADE_ORDER = [
  "K",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "HS",
] as const;
export type Grade = (typeof GRADE_ORDER)[number];

// "instruction" = Instruction level (evidence-driven), "enrolled" = Enrolled grade.
export type GradeMode = "instruction" | "enrolled";

export type ClusterNode = {
  id: string; // real CCSS code
  letter: string; // display pill text, e.g. "8A" — grade+letter for K-8, baked in at generation time so a merged domain's clusters stay disambiguated with no runtime tagging step; just the bare letter ("A") for HS, which has no per-cluster grade
  short: string;
  grade?: Grade; // set once domains are merged by code; undefined for HS
  standards: StandardNode[];
};

export type DomainNode = {
  id: string; // real CCSS code
  code: string; // grouping key mergeDomainsByCode unifies same-family domains on, e.g. "NF" (same value across 1.NF/2.NF/.../8.NF); for HS this equals id (domains don't merge across categories)
  name: string;
  grade?: Grade; // absent once merged — see ClusterNode.grade instead
  category?: string; // HS category name (e.g. "Algebra"); absent for K-8. Domains never merge across HS categories, so this is always readable directly off leaf.domain — no per-cluster copy needed.
  clusters: ClusterNode[];
};

export type LessonInfo = {
  // Optional only because the frozen demo capture predates the field; the
  // endpoint always sends it. See groupLessons for the fallback.
  lesson_id?: number;
  lesson_title: string;
  got: number;
  total: number;
  response_id: number;
};

export type StudentMasteryCell = {
  mastery: number;
  evidence: number;
  started: boolean;
  lessons: LessonInfo[];
};

export type StudentMastery = {
  user_id?: number;
  name: string;
  theta?: number;
  grade?: Grade; // matches the backend student table
  group?: string;
  teacher_user_ids?: number[];
  teacher?: string;
  cohort?: string;
  campus?: string;
  cells: Record<string, StudentMasteryCell>;
};

// Raw shape of GET /analytics/standards, before the client-side mapping in
// StandardsViewContent normalizes `grade`/`cells` into StudentMastery.
export type StandardsResponse = {
  computed_at: string | null; // ISO 8601; when the nightly cache last refreshed. null => never computed
  students: Array<{
    user_id: number;
    name: string;
    teacher_user_ids: number[];
    grade: Grade | null;
    cells: Record<string, StudentMasteryCell>;
  }>;
};

export type MasteryTimelinePoint = {
  date: string; // "YYYY-MM-DD"
  mastery: number; // 0..1
};

// Response shape of GET /analytics/standards/timeline.
export type MasteryTimelineResponse = {
  computed_at: string | null;
  current_mastery: number | null; // 0..1; the grid tile's own cached value
  points: MasteryTimelinePoint[]; // ascending by date, may be empty
  lessons: LessonInfo[];
  // True when replaying the attempt history no longer lands on current_mastery
  // — a past response was edited, a lesson retagged, or the mastery code moved
  // — so the series is a reconstruction rather than this cell's history until
  // the nightly job rebuilds.
  is_stale: boolean;
};

export type MasteryCellInfo = {
  mastery: number;
  started: boolean;
  evidence: number;
  code: string | null;
  isAggregate?: boolean;
  total?: number;
  startedCount?: number;
};

export type MasteryCategory = {
  key: MasteryLevel;
  label: string;
  dot: string;
  pill: string;
  pillInk: string;
};
